/**
 * Booking-Triggered Pricing Service
 *
 * Automatically triggers pricing rule evaluation when booking changes are detected.
 * Uses debouncing to prevent excessive repricing during bulk operations.
 */

import { PRICING_CONFIG } from '../config/pricingConfig';
import type { BookingSyncResult } from './bookingOccupancyService';
import { isApplyInProgressError } from './edgeFunctionErrors';

export type BookingChangeAggregation = {
  bookingsCreated: number;
  bookingsUpdated: number;
  bookingsCancelled: number;
  affectedDates: Set<string>;
  firstChange: Date;
  lastChange: Date;
};

type RepricingCallback = (
  propertyId: string,
  aggregation: BookingChangeAggregation
) => Promise<void>;

class BookingTriggeredPricingService {
  private debouncedTriggers = new Map<string, NodeJS.Timeout>();
  private pendingChanges = new Map<string, BookingChangeAggregation>();
  private lockRetryAttempts = new Map<string, number>();
  private repricingCallback: RepricingCallback | null = null;
  private enabled = true;

  /**
   * Set the callback that will be invoked when repricing should occur
   */
  setRepricingCallback(callback: RepricingCallback): void {
    this.repricingCallback = callback;
  }

  /**
   * Enable or disable automatic repricing triggers
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.clearAllPending();
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Handle a booking change event.
   * Aggregates changes during the debounce window and triggers repricing after.
   */
  handleBookingChange(
    propertyId: string,
    syncResult: BookingSyncResult,
    affectedDates?: string[]
  ): void {
    if (!this.enabled) {
      console.log(
        '[BookingTriggeredPricingService] Disabled, ignoring booking change'
      );
      return;
    }

    const hasChanges =
      syncResult.bookingsCreated > 0 ||
      syncResult.bookingsUpdated > 0 ||
      syncResult.bookingsCancelled > 0;

    if (!hasChanges) {
      console.log(
        '[BookingTriggeredPricingService] No booking changes detected, skipping'
      );
      return;
    }

    console.log(
      `[BookingTriggeredPricingService] Booking change detected for property ${propertyId}:`,
      {
        created: syncResult.bookingsCreated,
        updated: syncResult.bookingsUpdated,
        cancelled: syncResult.bookingsCancelled,
      }
    );

    // Aggregate changes during debounce window
    this.aggregateChanges(propertyId, syncResult, affectedDates);

    // Reset/set debounce timer
    const existing = this.debouncedTriggers.get(propertyId);
    if (existing) {
      clearTimeout(existing);
    }

    const timeout = setTimeout(() => {
      this.executeRepricing(propertyId);
    }, PRICING_CONFIG.BOOKING_TRIGGER_DEBOUNCE_MS);

    this.debouncedTriggers.set(propertyId, timeout);

    console.log(
      `[BookingTriggeredPricingService] Debounce timer set for property ${propertyId} (${PRICING_CONFIG.BOOKING_TRIGGER_DEBOUNCE_MS}ms)`
    );
  }

  /**
   * Force immediate execution of pending repricing for a property
   */
  async flushPending(propertyId: string): Promise<void> {
    const existing = this.debouncedTriggers.get(propertyId);
    if (existing) {
      clearTimeout(existing);
      this.debouncedTriggers.delete(propertyId);
    }

    if (this.pendingChanges.has(propertyId)) {
      await this.executeRepricing(propertyId);
    }
  }

  /**
   * Clear all pending triggers (useful when disabling)
   */
  clearAllPending(): void {
    for (const timeout of this.debouncedTriggers.values()) {
      clearTimeout(timeout);
    }
    this.debouncedTriggers.clear();
    this.pendingChanges.clear();
    this.lockRetryAttempts.clear();
  }

  /**
   * Get pending changes for a property (for debugging/monitoring)
   */
  getPendingChanges(propertyId: string): BookingChangeAggregation | undefined {
    return this.pendingChanges.get(propertyId);
  }

  private aggregateChanges(
    propertyId: string,
    syncResult: BookingSyncResult,
    affectedDates?: string[]
  ): void {
    const now = new Date();
    const existing = this.pendingChanges.get(propertyId);

    if (existing) {
      existing.bookingsCreated += syncResult.bookingsCreated;
      existing.bookingsUpdated += syncResult.bookingsUpdated;
      existing.bookingsCancelled += syncResult.bookingsCancelled;
      existing.lastChange = now;

      if (affectedDates) {
        for (const date of affectedDates) {
          existing.affectedDates.add(date);
        }
      }
    } else {
      this.pendingChanges.set(propertyId, {
        bookingsCreated: syncResult.bookingsCreated,
        bookingsUpdated: syncResult.bookingsUpdated,
        bookingsCancelled: syncResult.bookingsCancelled,
        affectedDates: new Set(affectedDates || []),
        firstChange: now,
        lastChange: now,
      });
    }
  }

  private requeue(
    propertyId: string,
    changes: BookingChangeAggregation,
    delayMs: number
  ): void {
    const newer = this.pendingChanges.get(propertyId);
    if (newer) {
      newer.bookingsCreated += changes.bookingsCreated;
      newer.bookingsUpdated += changes.bookingsUpdated;
      newer.bookingsCancelled += changes.bookingsCancelled;
      newer.firstChange = changes.firstChange;
      for (const date of changes.affectedDates) {
        newer.affectedDates.add(date);
      }
      return;
    }

    this.pendingChanges.set(propertyId, changes);
    this.debouncedTriggers.set(
      propertyId,
      setTimeout(() => {
        this.executeRepricing(propertyId);
      }, delayMs)
    );
  }

  private async executeRepricing(propertyId: string): Promise<void> {
    const changes = this.pendingChanges.get(propertyId);
    this.debouncedTriggers.delete(propertyId);
    this.pendingChanges.delete(propertyId);

    if (!changes) {
      console.log(
        `[BookingTriggeredPricingService] No pending changes for property ${propertyId}`
      );
      return;
    }

    console.log(
      `[BookingTriggeredPricingService] Executing repricing for property ${propertyId}`,
      {
        created: changes.bookingsCreated,
        updated: changes.bookingsUpdated,
        cancelled: changes.bookingsCancelled,
        affectedDatesCount: changes.affectedDates.size,
        debounceWindow: `${changes.lastChange.getTime() - changes.firstChange.getTime()}ms`,
      }
    );

    if (this.repricingCallback) {
      try {
        await this.repricingCallback(propertyId, changes);
        this.lockRetryAttempts.delete(propertyId);
        console.log(
          `[BookingTriggeredPricingService] Repricing completed for property ${propertyId}`
        );
      } catch (error) {
        const retryDelays = PRICING_CONFIG.BOOKING_TRIGGER_LOCK_RETRY_DELAYS_MS;
        const attempt = this.lockRetryAttempts.get(propertyId) ?? 0;
        const retryDelayMs = retryDelays[attempt];
        if (
          this.enabled &&
          isApplyInProgressError(error) &&
          retryDelayMs !== undefined
        ) {
          this.lockRetryAttempts.set(propertyId, attempt + 1);
          console.warn(
            `[BookingTriggeredPricingService] Apply lock held for property ${propertyId}; retrying repricing in ${retryDelayMs}ms (retry ${attempt + 1}/${retryDelays.length})`
          );
          this.requeue(propertyId, changes, retryDelayMs);
          return;
        }
        this.lockRetryAttempts.delete(propertyId);
        console.error(
          `[BookingTriggeredPricingService] Repricing failed for property ${propertyId}:`,
          error
        );
      }
    } else {
      console.warn(
        '[BookingTriggeredPricingService] No repricing callback configured'
      );
    }
  }
}

export const bookingTriggeredPricingService =
  new BookingTriggeredPricingService();
